/** Offline numerical diagnostic, not a scientific-validation or source-admission
 * gate. Predictions and observations are supplied numbers in the same unit;
 * their provenance, physical validity and measurement assumptions are untested. */
export interface SharedOffsetInput {
  unit: string
  errorModel: 'hard-absolute-bound'
  observations: number[]
  errorBounds: number[]
  models: { id: string; predictions: number[] }[]
}

export interface OffsetConstraint {
  observationIndex: number
  residual: number
  lower: number
  upper: number
}

export interface SharedOffsetFit {
  modelId: string
  compatible: boolean
  constraints: OffsetConstraint[]
  greatestLowerBound: number
  leastUpperBound: number
  /** Null denotes an empty intersection, not an unknown or zero offset. */
  offsetInterval: { lower: number; upper: number } | null
}

export interface DiagnosticIssue {
  path: string
  reason:
    | 'missing-or-unknown'
    | 'wrong-shape'
    | 'nonfinite-number'
    | 'negative-bound'
    | 'length-mismatch'
    | 'empty'
    | 'duplicate-id'
    | 'unsupported-error-model'
    | 'arithmetic-overflow'
}

export type SharedOffsetDiagnostic = {
  boundary: 'supplied-numeric-common-offset-hard-bounds'
  interpretation: string
} & (
  | { kind: 'not-assessed'; issues: DiagnosticIssue[] }
  | {
      kind: 'one-compatible'
      unit: string
      compatibleModelIds: [string]
      fits: SharedOffsetFit[]
    }
  | {
      kind: 'inconclusive'
      reason: 'no-compatible-model' | 'multiple-compatible-models' | 'only-one-candidate'
      unit: string
      compatibleModelIds: string[]
      fits: SharedOffsetFit[]
    }
)

const BOUNDARY = 'supplied-numeric-common-offset-hard-bounds' as const
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/** Do not coerce null, empty strings, missing readings or standard deviations
 * into observed zeros/hard bounds. A rejected input cannot select a model. */
function validate(value: unknown): DiagnosticIssue[] {
  const issues: DiagnosticIssue[] = []
  const issue = (path: string, reason: DiagnosticIssue['reason']) => {
    issues.push({ path, reason })
  }
  if (!object(value)) {
    issue('$', value == null ? 'missing-or-unknown' : 'wrong-shape')
    return issues
  }
  const label = (item: unknown, path: string) => {
    if (typeof item !== 'string') issue(path, item == null ? 'missing-or-unknown' : 'wrong-shape')
    else if (!item.trim()) issue(path, 'empty')
  }
  const vector = (items: unknown, path: string, bounds = false) => {
    if (!Array.isArray(items)) {
      issue(path, items == null ? 'missing-or-unknown' : 'wrong-shape')
      return
    }
    if (!items.length) issue(path, 'empty')
    // A sparse array is a missing observation too, so do not use forEach/every,
    // which skip holes before JSON has had a chance to turn them into nulls.
    for (let i = 0; i < items.length; i++) {
      const item: unknown = items[i]
      const at = `${path}[${i}]`
      if (typeof item !== 'number') issue(at, item == null ? 'missing-or-unknown' : 'wrong-shape')
      else if (!Number.isFinite(item)) issue(at, 'nonfinite-number')
      else if (bounds && item < 0) issue(at, 'negative-bound')
    }
  }
  label(value.unit, 'unit')
  if (value.errorModel !== 'hard-absolute-bound')
    issue('errorModel', value.errorModel == null ? 'missing-or-unknown' : 'unsupported-error-model')
  vector(value.observations, 'observations')
  vector(value.errorBounds, 'errorBounds', true)
  const length = Array.isArray(value.observations) ? value.observations.length : undefined
  if (
    Array.isArray(value.errorBounds) &&
    length !== undefined &&
    value.errorBounds.length !== length
  )
    issue('errorBounds', 'length-mismatch')
  if (!Array.isArray(value.models)) {
    issue('models', value.models == null ? 'missing-or-unknown' : 'wrong-shape')
    return issues
  }
  if (!value.models.length) issue('models', 'empty')
  const ids = new Set<string>()
  for (let i = 0; i < value.models.length; i++) {
    const model: unknown = value.models[i]
    const at = `models[${i}]`
    if (!object(model)) {
      issue(at, model == null ? 'missing-or-unknown' : 'wrong-shape')
      continue
    }
    label(model.id, `${at}.id`)
    if (typeof model.id === 'string') {
      if (ids.has(model.id)) issue(`${at}.id`, 'duplicate-id')
      ids.add(model.id)
    }
    vector(model.predictions, `${at}.predictions`)
    if (
      Array.isArray(model.predictions) &&
      length !== undefined &&
      model.predictions.length !== length
    )
      issue(`${at}.predictions`, 'length-mismatch')
  }
  return issues
}

/** For y_i = prediction_i + c + e_i, |e_i| <= b_i, every reading
 * permits c in [y_i-prediction_i-b_i, y_i-prediction_i+b_i]. One common
 * c must satisfy their intersection. Mean contrasts discard these constraints;
 * residual range <= 2b is equivalent only when all hard bounds equal b.
 *
 * Uses finite JavaScript number arithmetic and inclusive comparisons, with no
 * hidden tolerance. It is not an exact-arithmetic certificate near rounding
 * boundaries. Overflow is rejected rather than interpreted as compatibility. */
export function diagnoseSharedOffset(value: unknown): SharedOffsetDiagnostic {
  const issues = validate(value)
  const notAssessed = (): SharedOffsetDiagnostic => ({
    boundary: BOUNDARY,
    kind: 'not-assessed',
    issues,
    interpretation: 'The supplied numeric constraints cannot be assessed. No model is selected.',
  })
  if (issues.length) return notAssessed()
  const input = value as SharedOffsetInput
  const fits: SharedOffsetFit[] = []
  for (let m = 0; m < input.models.length; m++) {
    const model = input.models[m]!
    const constraints: OffsetConstraint[] = []
    let greatestLowerBound = -Infinity
    let leastUpperBound = Infinity
    for (let i = 0; i < input.observations.length; i++) {
      const residual = input.observations[i]! - model.predictions[i]!
      const lower = residual - input.errorBounds[i]!
      const upper = residual + input.errorBounds[i]!
      if (![residual, lower, upper].every(Number.isFinite)) {
        issues.push({ path: `models[${m}].predictions[${i}]`, reason: 'arithmetic-overflow' })
        continue
      }
      constraints.push({ observationIndex: i, residual, lower, upper })
      greatestLowerBound = Math.max(greatestLowerBound, lower)
      leastUpperBound = Math.min(leastUpperBound, upper)
    }
    const compatible = greatestLowerBound <= leastUpperBound
    fits.push({
      modelId: model.id,
      compatible,
      constraints,
      greatestLowerBound,
      leastUpperBound,
      offsetInterval: compatible ? { lower: greatestLowerBound, upper: leastUpperBound } : null,
    })
  }
  // A missing/overflowing candidate must not disappear and leave another one
  // looking uniquely compatible with a comparison that was never completed.
  if (issues.length) return notAssessed()
  const compatibleModelIds = fits.filter((fit) => fit.compatible).map((fit) => fit.modelId)
  if (compatibleModelIds.length === 1 && fits.length > 1) {
    return {
      boundary: BOUNDARY,
      kind: 'one-compatible',
      unit: input.unit,
      compatibleModelIds: [compatibleModelIds[0]!],
      fits,
      interpretation:
        'Exactly one supplied candidate fits all numeric constraints under the declared assumptions. This does not establish model truth, statistical significance, external validity or source admission.',
    }
  }
  const reason =
    compatibleModelIds.length === 0
      ? 'no-compatible-model'
      : fits.length === 1
        ? 'only-one-candidate'
        : 'multiple-compatible-models'
  return {
    boundary: BOUNDARY,
    kind: 'inconclusive',
    reason,
    unit: input.unit,
    compatibleModelIds,
    fits,
    interpretation:
      reason === 'no-compatible-model'
        ? 'No supplied candidate fits all numeric constraints. The observations do not select a model; the predictions, data or measurement assumptions may need revisiting.'
        : reason === 'only-one-candidate'
          ? 'The single supplied candidate fits, but no alternative was supplied for comparison. Compatibility does not establish truth.'
          : 'Multiple supplied candidates fit. These observations do not distinguish them; this does not establish equivalence or identical behavior elsewhere.',
  }
}
